import { useMemo, useSyncExternalStore } from 'react';
import { motion } from 'motion/react';
import Shell from './components/dashboard/shell';
import SignIn from './components/auth/SignIn';
import ResetPassword from './components/auth/ResetPassword';
import { OnboardingGate, InviteFlow } from './components/auth/Onboarding';
import TelemetryLogo from './components/dashboard/logo';
import { useAuth } from './hooks/useAuth';
import { ThemeProvider } from './contexts/ThemeContext';
import BootScreen from './components/boot-screen';
import { recoveryStore } from './lib/recovery';

const DEMO_EMAIL = (import.meta.env.VITE_DEMO_EMAIL as string | undefined) ?? '';


export default function App() {
  const { session, loading, signIn, signUp, signOut } = useAuth();
  const inviteToken = useMemo(() => new URLSearchParams(window.location.search).get('invite'), []);
  const resetting = useSyncExternalStore(recoveryStore.subscribe, recoveryStore.getSnapshot);

  // Marketing site (opsalo.com) links "Get Started" here with ?mode=signup
  // so it opens straight on the sign-up tab instead of sign-in.
  const startMode = useMemo(() => {
    const mode = new URLSearchParams(window.location.search).get('mode');
    return mode === 'signup' ? 'signup' : 'signin';
  }, []);

  // The splash spinner runs before we know the user (or their theme
  // preference) yet, so it intentionally stays fixed on the original
  // all-dark look rather than reading from ThemeContext.
  if (loading) return <BootScreen />;

  return (
    <ThemeProvider>
      {resetting ? (
        // Password reset (code or link): must come first, since it signs the user in.
        <ResetPassword onDone={() => recoveryStore.clear()} />
      ) : inviteToken ? (
        // Invite link handles its own auth + acceptance, signed in or out.
        <InviteFlow token={inviteToken} session={session} signIn={signIn} signUp={signUp} />
      ) : !session ? (
        <SignIn onSignIn={signIn} onSignUp={signUp} defaultEmail={DEMO_EMAIL} startMode={startMode} />
      ) : (
        <div className="min-h-screen">
          <OnboardingGate authUserId={session.user.id} email={session.user.email ?? ''} userName={(session.user.user_metadata?.name as string | undefined) ?? ''} onSignOut={() => void signOut()} />
        </div>
      )}
    </ThemeProvider>
  );
}